import { useSteps, StepLayoutChildProps, Step } from '@consigli/facade';
import { BuildingPackage, BuildingPackageType } from '@consigli/types';
import { FC, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { CeilingViewerContainer } from '@/features/ceiling-viewer/container';

import { CeilingWizardLayout } from './ceiling-wizard-layout';
import { SelectFloorsStep } from './select-floors-step';
import { UploadFilesStep } from './upload-files';
import { useCreatePackageUtil } from '../hooks/use-create-package';
import { useUploadProgress } from '../hooks/use-upload-progress';

export type CeilingGridResponse = Promise<BuildingPackage | undefined>;

export const CeilingWizard = () => {
  const { t } = useTranslation();

  const methods = useForm<FieldValues>({
    defaultValues: {
      iterationName: '',
      inputType: '',
    },
  });
  const { getValues } = methods;

  const [localFiles, setLocalFiles] = useState<File[]>([]);
  const [targetCounter, setTargetCounter] = useState(0);
  const [currentCounter, setCurrentCounter] = useState(0);
  const { onAnalyze: onPackageCreation, startAnalysis } = useCreatePackageUtil({
    setCurrentCounter,
    setTargetCounter,
  });
  const [createdCeilingGridId, setCreatedCeilingGridId] = useState<string>();

  const onCeilingGridCreation = async (): CeilingGridResponse => {
    try {
      const response = await onPackageCreation(
        localFiles,
        getValues(),
        BuildingPackageType.CEILING_GRID,
      );
      if (response && response.type === BuildingPackageType.CEILING_GRID) {
        setCreatedCeilingGridId(response.id);
        return response;
      }
    } catch (error) {
      console.log('Error analyzing ceiling:', error);
      toast.error(t(`package-creation.failed-analyzed`));
      return undefined;
    }
  };

  const { isUploading, progress } = useUploadProgress(currentCounter, targetCounter);

  const { withStep, StepIndicator, StepOutlet, useStepContext } = useSteps(
    (step: (name: string, component: FC<StepLayoutChildProps>) => Step) => [
      step(t('createproject.uploadfiles'), () => (
        <FormProvider {...methods}>
          <UploadFilesStep
            localFiles={localFiles}
            setLocalFiles={setLocalFiles}
            onCeilingGridCreation={onCeilingGridCreation}
            startAnalysis={startAnalysis}
            useStepContext={useStepContext}
          />
        </FormProvider>
      )),
      step(t('package-creation.ceiling-grid.select-floors'), () => (
        <SelectFloorsStep useStepContext={useStepContext} />
      )),
      step(t('package-creation.ceiling-grid.floor-configuration'), () => (
        <CeilingViewerContainer
          useStepContext={useStepContext}
          createdCeilingGridId={createdCeilingGridId || ''}
        />
      )),
    ],
  );

  const stepsRenderer = withStep(() => (
    <>
      <StepIndicator />
      <StepOutlet />
    </>
  ));

  return (
    <CeilingWizardLayout
      stepsRenderer={stepsRenderer}
      isUploading={isUploading}
      progress={progress}
    />
  );
};
